import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FieldValues, SubmitHandler, UseFormReturn } from 'react-hook-form';

const DEFAULT_AUTOSAVE_DELAY_IN_MS = 1000;

export type UseFormAutosaveProps<TFieldValues extends FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  onSave: SubmitHandler<TFieldValues>;
  delay?: number;
};

export default function useFormAutosave<TFieldValues extends FieldValues>({
  form,
  onSave,
  delay = DEFAULT_AUTOSAVE_DELAY_IN_MS,
}: UseFormAutosaveProps<TFieldValues>) {
  const { watch, handleSubmit } = form;

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const submit = useMemo(
    () => handleSubmit(fields => onSaveRef.current(fields)),
    [handleSubmit],
  );

  const debouncedSubmit = useMemo(
    () => debounce(submit, delay),
    [submit, delay],
  );

  const debouncedSubmitRef = useRef(debouncedSubmit);
  debouncedSubmitRef.current = debouncedSubmit;

  useEffect(() => {
    const subscription = watch((_, { type }) => {
      if (type === 'change') debouncedSubmit();
    });

    return () => subscription.unsubscribe();
  }, [watch, debouncedSubmit]);

  useEffect(
    () => () => {
      debouncedSubmitRef.current.flush();
    },
    [],
  );

  const save = useCallback(() => {
    debouncedSubmit.cancel();
    return submit();
  }, [debouncedSubmit, submit]);

  const cancel = useCallback(() => debouncedSubmit.cancel(), [debouncedSubmit]);

  return { save, cancel };
}
